import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The wizard's draft saver (`observers-page-draft`, FR2/FR3).
 *
 * A step whose edits are written into the plan store as they are made switches this on, and the
 * plan then saves itself: about a second after the last edit, never more than five seconds after
 * the first unsaved one, and one save at a time. The step loses its Save button; the title bar's
 * *Draft saved* tag reads this hook's state instead.
 *
 * ⚠ **Step-agnostic, and it must stay that way.** Autosave is rolling out to every onboarding page
 * (maria@twinlabs.ai, 2026-10-02), so the next refactored step switches this on rather than growing
 * its own saver. Nothing here names a step; the page decides which steps it serves.
 *
 * ⚠ **Only `request()` starts a save — this hook never subscribes to the store.** Loading the
 * running configuration writes into the plan store and then restores the clean flag; a hook that
 * saved on every store change would turn every load into a save of a plan nobody edited. A PAGE may
 * subscribe and call `request()` itself, but only where it filters loads out before asking — the
 * Floor Plan step's watcher re-reads the dirty flag after the call stack finishes, and the
 * drawn-against records' subscription skips a restore (`floor-plan-page-draft`). The `isClean`
 * check at fire time is the other half: a request that a load (or another save) has overtaken
 * costs nothing.
 *
 * **Outbound bounds.** The hook makes no request of its own — `save` is the page's `persistPlan`,
 * whose requests carry the middleware's 30 s per-attempt timeout. There is **no automatic retry**:
 * a plan save is a write whose idempotency is the server's to prove, and an unattended failing save
 * must not hammer it. A failure stays on screen until the operator presses Retry or edits again.
 */

/** Quiet time after the last request before the save fires. */
export const DRAFT_SAVE_DEBOUNCE_MS = 1_000;
/** The longest a request waits, counted from the first unsaved one, so continuous editing saves. */
export const DRAFT_SAVE_MAX_WAIT_MS = 5_000;
/** How often `settle()` looks again while a save, or its follow-up, is still running. */
const DRAFT_SETTLE_POLL_MS = 50;

/** What a save reports. Structurally the page's `PlanSaveOutcome`; declared here so the hook imports no page. */
export type DraftSaveOutcome = { saved: true } | { saved: false; reason: string | null };

/**
 * - `saved` — nothing pending, nothing in flight, and the last attempt (if any) succeeded.
 * - `saving` — a request is waiting for its timer, or a save is in flight.
 * - `failed` — the last attempt failed and nothing newer is pending. `reason` says why, or `null`.
 * - `blocked` — a save cannot be attempted at all; `reason` is the caller's sentence.
 */
export type DraftSaveStatus = 'saved' | 'saving' | 'failed' | 'blocked';

export interface DraftSaverOptions {
  save: () => Promise<DraftSaveOutcome>;
  /** Whether new requests are accepted. A pending or in-flight save still completes when this goes false. */
  enabled: boolean;
  /**
   * Why a save cannot be attempted now, or `null`. While set, a request is recorded and nothing is
   * sent; when it clears with a request recorded, exactly one save follows.
   */
  blocked: { reason: string } | null;
  /** Read at fire time. True defers the save by one debounce rather than dropping it. */
  isSuspended: () => boolean;
  /** Read at fire time. True means there is nothing to save, so nothing is sent. */
  isClean: () => boolean;
}

export interface DraftSaver {
  status: DraftSaveStatus;
  /** When the last successful save THIS session landed; `null` before one. */
  savedAt: Date | null;
  /** `failed`: the save's own reason, or `null`. `blocked`: the caller's sentence. Otherwise `null`. */
  reason: string | null;
  /** Schedule a save (debounced, with the maximum wait). */
  request: () => void;
  /** Fire a waiting save now. Does nothing when none is waiting. */
  flush: () => void;
  /** Drop a waiting save, and any follow-up, without firing it. A save already in flight completes. */
  cancel: () => void;
  /** Save now — the tag's Retry. */
  retry: () => void;
  /**
   * Record a successful plan save this hook did not make — the editor's own Save, Go Live's
   * pre-flight. The draft is the whole plan document, so any save that landed is the draft saved:
   * the time moves on and an earlier failure no longer describes it.
   */
  noteSaved: () => void;
  /**
   * Fire a waiting save now and resolve once nothing is pending or in flight — for an exit that
   * would otherwise ask the operator to discard work this saver was about to persist. A save that
   * is blocked is not waited for: it cannot happen, and the store stays dirty to say so.
   */
  settle: () => Promise<void>;
  /** Forget everything — timers, follow-up, the last save and the last failure. For a change of twin. */
  reset: () => void;
}

export const useDraftSaver = (options: DraftSaverOptions): DraftSaver => {
  // The latest options, read by timers that fire long after the render that armed them.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxWaitRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  /** A request matured while a save was in flight: one more save when it lands. */
  const followUpRef = useRef(false);
  /** A request arrived (or matured) while blocked: one save when the block clears. */
  const blockedPendingRef = useRef(false);
  const mountedRef = useRef(true);
  /** Bumped by `cancel` and `reset`: a save that started before either may not ask for another. */
  const epochRef = useRef(0);
  /** Bumped by `reset` alone — a change of twin: an older save's outcome is not this twin's to show. */
  const generationRef = useRef(0);

  const [pending, setPending] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [failure, setFailure] = useState<{ reason: string | null } | null>(null);

  const clearTimers = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (maxWaitRef.current) clearTimeout(maxWaitRef.current);
    debounceRef.current = null;
    maxWaitRef.current = null;
  };

  const syncPending = () => {
    if (!mountedRef.current) return;
    setPending(
      debounceRef.current != null ||
        maxWaitRef.current != null ||
        followUpRef.current ||
        blockedPendingRef.current,
    );
  };

  // A ref to `fire`, so the timers it arms and the save it awaits always call the current one.
  const fireRef = useRef<() => void>(() => undefined);

  const armDebounce = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fireRef.current(), DRAFT_SAVE_DEBOUNCE_MS);
  };

  fireRef.current = () => {
    clearTimers();
    if (inFlightRef.current) {
      followUpRef.current = true;
      syncPending();
      return;
    }
    const current = optionsRef.current;
    if (current.blocked) {
      blockedPendingRef.current = true;
      syncPending();
      return;
    }
    if (current.isSuspended()) {
      // Deferred, not dropped: whatever suspended it (Go Live) decides afterwards whether the
      // request still stands, by cancelling it or not.
      armDebounce();
      syncPending();
      return;
    }
    if (current.isClean()) {
      // Overtaken — by a load, or by another save that carried the same work. Nothing to send, and
      // an earlier failure no longer describes the plan.
      setFailure(null);
      syncPending();
      return;
    }

    inFlightRef.current = true;
    setInFlight(true);
    syncPending();
    const epoch = epochRef.current;
    const generation = generationRef.current;
    void (async () => {
      let outcome: DraftSaveOutcome;
      try {
        outcome = await current.save();
      } catch {
        // `persistPlan` reports rather than throws; a throw is still a save that did not happen.
        outcome = { saved: false, reason: null };
      }
      inFlightRef.current = false;
      if (!mountedRef.current) return;
      setInFlight(false);
      if (generation !== generationRef.current) {
        // Reset since this save started: its outcome is not the new twin's to show. A follow-up
        // that matured during it IS the new twin's work, so it runs.
        if (followUpRef.current) {
          followUpRef.current = false;
          fireRef.current();
        } else {
          syncPending();
        }
        return;
      }
      if (outcome.saved) {
        setSavedAt(new Date());
        setFailure(null);
      } else {
        setFailure({ reason: outcome.reason });
      }
      if (followUpRef.current) {
        followUpRef.current = false;
        fireRef.current();
      } else if (
        outcome.saved &&
        epoch === epochRef.current &&
        !debounceRef.current &&
        !maxWaitRef.current &&
        !optionsRef.current.isSuspended() &&
        !optionsRef.current.isClean()
      ) {
        /*
         * Saved, yet the store is still dirty with nothing waiting: something changed the plan while
         * this save was in flight without asking for a save — a live-configuration re-match, which
         * must never request one itself. Its change is real and unsaved, so carry it in one more
         * save rather than leave a dirty store behind a tag that says *Draft saved*.
         */
        armDebounce();
        syncPending();
      } else {
        syncPending();
      }
    })();
  };

  /** Arm the timers for a save already accepted. A block is decided at fire time, like suspension. */
  const schedule = () => {
    armDebounce();
    if (!maxWaitRef.current) {
      maxWaitRef.current = setTimeout(() => fireRef.current(), DRAFT_SAVE_MAX_WAIT_MS);
    }
    syncPending();
  };

  const request = useCallback(() => {
    // Never after unmount: the cleanup has cleared the timers, and a late request (a page-level
    // watcher's continuation, say) would arm one that saves with no tag left to report it.
    if (!optionsRef.current.enabled || !mountedRef.current) return;
    schedule();
    // The helpers read refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flush = useCallback(() => {
    if (debounceRef.current || maxWaitRef.current) fireRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancel = useCallback(() => {
    epochRef.current += 1;
    clearTimers();
    followUpRef.current = false;
    blockedPendingRef.current = false;
    syncPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = useCallback(() => fireRef.current(), []);

  const settle = useCallback(async () => {
    const waiting = () => debounceRef.current != null || maxWaitRef.current != null;
    while (
      mountedRef.current &&
      (inFlightRef.current || followUpRef.current || waiting()) &&
      !optionsRef.current.isSuspended()
    ) {
      // A timer armed while a save was in flight — an edit, or the dirty-store follow-up — is
      // fired as soon as nothing is in flight, rather than left for the exit to cancel.
      if (!inFlightRef.current && waiting()) fireRef.current();
      await new Promise(resolve => setTimeout(resolve, DRAFT_SETTLE_POLL_MS));
    }
  }, []);

  const reset = useCallback(() => {
    epochRef.current += 1;
    generationRef.current += 1;
    setInFlight(false);
    clearTimers();
    followUpRef.current = false;
    blockedPendingRef.current = false;
    syncPending();
    setSavedAt(null);
    setFailure(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noteSaved = useCallback(() => {
    if (!mountedRef.current) return;
    setSavedAt(new Date());
    setFailure(null);
  }, []);

  // The block cleared with a request recorded: exactly one save — even off the step that asked for
  // it (the operator usually clears duplicate codes on Floor Plan). It was accepted already, so it
  // is scheduled without the eligibility check a NEW request gets.
  const isBlocked = options.blocked != null;
  useEffect(() => {
    if (isBlocked || !blockedPendingRef.current) return;
    blockedPendingRef.current = false;
    schedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlocked]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimers();
    };
  }, []);

  const status: DraftSaveStatus = options.blocked
    ? 'blocked'
    : pending || inFlight
      ? 'saving'
      : failure
        ? 'failed'
        : 'saved';
  const reason =
    status === 'blocked'
      ? (options.blocked?.reason ?? null)
      : status === 'failed'
        ? (failure?.reason ?? null)
        : null;

  return { status, savedAt, reason, request, flush, cancel, retry, noteSaved, settle, reset };
};
